// Qué se sabe de una imagen generada, leído de su fila —
// ADR-2026-09-16-la-card-de-info-del-lienzo.
//
// `datos_generacion` es el body del pedido que la generó, tal cual (con lo que el backend
// le sumó). De ahí salen los ids: los productos (`variantesDeImagen`, que ya sabe leer
// columna y jsonb; para las imágenes del chat, `reference_images[].product_id`), la
// modelo guardada (`modelo_guardado_id`, y `modelo_config[]` en las grupales) y el fondo
// (`background_mode` + `background_id` o `background_file_name`, la misma inversa que
// `fondoDePedido` en `configurarRegeneracion.ts`). La resolución viene en `ancho`/`alto`
// en las filas nuevas; sin eso, quien llama mide la imagen. El peso no se guarda en
// ningún lado: se le pide al storage.
//
// Los ids se cruzan con los catálogos que ya tiene el chat; lo que no está en el
// catálogo (borrado, de otra marca) simplemente no aparece. Puro y testeado.

import type { DatosCelda } from '../../pages/v2/components/useDatosDeCeldas';
import type { FondoBiblioteca, FondoGuardado } from '../../pages/v2/fondosApi';
import { variantesDeImagen } from '../../utils/variantesDeImagen';

export interface ProductoParaInfo {
  id: string | number;
  name: string;
  imageUrl: string;
}
export interface ModeloParaInfo {
  id: string | number;
  nombre: string;
  thumbnailUrl: string;
}
export type FondoParaInfo =
  | { tipo: 'biblioteca'; fondo: FondoBiblioteca }
  | { tipo: 'guardado'; fondo: FondoGuardado };

export interface CatalogoParaInfo {
  productos: ProductoParaInfo[];
  modelos: ModeloParaInfo[];
  fondosGuardados: FondoGuardado[];
  fondosBiblioteca: FondoBiblioteca[];
}

export interface InfoDeImagen {
  resolucion: { ancho: number; alto: number } | null;
  productos: ProductoParaInfo[];
  modelos: ModeloParaInfo[];
  fondos: FondoParaInfo[];
}

export type FondoPedido = { tipo: 'biblioteca'; id: number } | { tipo: 'guardado'; fileName: string };

const objeto = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
const texto = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const lista = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? v.map(objeto) : [];
const entero = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
};

/** Ids de variante (los del catálogo del chat), en orden y sin repetir. */
export function idsDeProductos(datos: DatosCelda): string[] {
  const pedido = objeto(datos.datosGeneracion);
  const porFila = variantesDeImagen({
    variantes_ids: datos.variantesIds,
    variante_id: datos.varianteId,
    datos_generacion: pedido,
  });
  if (porFila.length > 0) return porFila;
  // Las del chat no llevan `variante_id`: el producto viaja como referencia.
  const out: string[] = [];
  for (const r of lista(pedido.reference_images)) {
    const id = texto(r.product_id).toLowerCase();
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

/** Ids de modelos guardadas: la principal y, en una grupal, las de cada slot. */
export function idsDeModelos(datos: DatosCelda): string[] {
  const pedido = objeto(datos.datosGeneracion);
  const out: string[] = [];
  const sumar = (v: unknown) => {
    const id = texto(v).toLowerCase();
    if (id && !out.includes(id)) out.push(id);
  };
  sumar(pedido.modelo_guardado_id);
  for (const slot of lista(pedido.modelo_config)) sumar(slot.modelo_guardado_id);
  return out;
}

/** Qué fondo pidió la fila, si fue uno del catálogo (biblioteca) o uno guardado. */
export function fondoPedido(datos: DatosCelda): FondoPedido | null {
  const pedido = objeto(datos.datosGeneracion);
  const modo = texto(pedido.background_mode);
  if (modo === 'elegido') {
    const id = entero(pedido.background_id);
    return id !== null ? { tipo: 'biblioteca', id } : null;
  }
  if (modo === 'guardado') {
    const fileName = texto(pedido.background_file_name);
    return fileName ? { tipo: 'guardado', fileName } : null;
  }
  return null;
}

/** `ancho`/`alto` que el backend midió al guardar; `null` en filas viejas o del editor. */
export function resolucionDeFila(datos: DatosCelda): { ancho: number; alto: number } | null {
  const pedido = objeto(datos.datosGeneracion);
  const ancho = entero(pedido.ancho);
  const alto = entero(pedido.alto);
  return ancho !== null && alto !== null ? { ancho, alto } : null;
}

export function resolverInfo(datos: DatosCelda, catalogo: CatalogoParaInfo): InfoDeImagen {
  const productos = idsDeProductos(datos).flatMap((id) => {
    const p = catalogo.productos.find((x) => String(x.id).toLowerCase() === id);
    return p ? [p] : [];
  });
  const modelos = idsDeModelos(datos).flatMap((id) => {
    const m = catalogo.modelos.find((x) => String(x.id).toLowerCase() === id);
    return m ? [m] : [];
  });
  const fondos: FondoParaInfo[] = [];
  const pedido = fondoPedido(datos);
  if (pedido?.tipo === 'biblioteca') {
    const f = catalogo.fondosBiblioteca.find((x) => x.id === pedido.id);
    if (f) fondos.push({ tipo: 'biblioteca', fondo: f });
  } else if (pedido?.tipo === 'guardado') {
    const f = catalogo.fondosGuardados.find((x) => x.fileName === pedido.fileName);
    if (f) fondos.push({ tipo: 'guardado', fondo: f });
  }
  return { resolucion: resolucionDeFila(datos), productos, modelos, fondos };
}
